import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GENERIC_ERROR_KEY, initialDealerAccessV2Record } from './data/DealerAccessV2Record';

const cardErrorFor = (dealerId: string) => ({
  id: dealerId,
  messageLangKey: GENERIC_ERROR_KEY,
});

const dealerAccessV2Slice = createSlice({
  name: 'dealerAccessV2',
  initialState: initialDealerAccessV2Record,
  reducers: {
    searchChanged(state, action: PayloadAction<{ term: string }>) {
      state.search = action.payload.term;
    },

    readMoreOpened(state) {
      state.isReadMoreOpen = true;
    },

    readMoreClosed(state) {
      state.isReadMoreOpen = false;
    },

    addPanelToggled(state, action: PayloadAction<{ dealerId: string }>) {
      state.addPanelDealerId =
        state.addPanelDealerId === action.payload.dealerId ? null : action.payload.dealerId;
      state.cardError = null;
    },

    addPanelClosed(state) {
      state.addPanelDealerId = null;
      state.cardError = null;
    },

    addSucceeded(state) {
      state.addPanelDealerId = null;
      state.cardError = null;
    },

    addFailed(state, action: PayloadAction<{ dealerId: string }>) {
      state.cardError = cardErrorFor(action.payload.dealerId);
    },

    editPanelToggled(state, action: PayloadAction<{ dealerId: string; isLimited: boolean }>) {
      const isClosing = state.editPanelDealerId === action.payload.dealerId;
      state.editPanelDealerId = isClosing ? null : action.payload.dealerId;
      state.editPanelLimited = isClosing ? false : action.payload.isLimited;
      state.cardError = null;
    },

    editPanelClosed(state) {
      state.editPanelDealerId = null;
      state.cardError = null;
    },

    modifySucceeded(state) {
      state.editPanelDealerId = null;
      state.cardError = null;
    },

    editPanelLimitedChanged(state, action: PayloadAction<{ isLimited: boolean }>) {
      state.editPanelLimited = action.payload.isLimited;
    },

    modifyFailed(state, action: PayloadAction<{ dealerId: string }>) {
      state.cardError = cardErrorFor(action.payload.dealerId);
    },

    removeConfirmOpened(state, action: PayloadAction<{ dealerId: string }>) {
      state.removeConfirmDealerId = action.payload.dealerId;
      state.cardError = null;
    },

    removeConfirmClosed(state) {
      state.removeConfirmDealerId = null;
    },

    removeSucceeded(state) {
      state.removeConfirmDealerId = null;
      state.editPanelDealerId = null;
      state.editPanelLimited = false;
      state.cardError = null;
    },

    removeFailed(state, action: PayloadAction<{ dealerId: string }>) {
      state.removeConfirmDealerId = null;
      state.cardError = cardErrorFor(action.payload.dealerId);
    },

    cardErrorCleared(state) {
      state.cardError = null;
    },
  },
});

export const {
  searchChanged,
  readMoreOpened,
  readMoreClosed,
  addPanelToggled,
  addPanelClosed,
  addSucceeded,
  addFailed,
  editPanelToggled,
  editPanelClosed,
  modifySucceeded,
  editPanelLimitedChanged,
  modifyFailed,
  removeConfirmOpened,
  removeConfirmClosed,
  removeSucceeded,
  removeFailed,
  cardErrorCleared,
} = dealerAccessV2Slice.actions;

export default dealerAccessV2Slice.reducer;
